import type { CalendarDate, ZonedDateTime } from '@internationalized/date';
import {
  OperationType,
  PriceListType,
} from '@rematter/frontend-lib-api-client';
import { atom, getDefaultStore } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import type { SelectedCell } from '../_components/_shared/hooks/useProductPricingColumns';
import { defaultPriceListGetOrCreateMutationAtom } from '../_graphql/queryDefaultPriceListGetOrCreate';
import {
  EvaluatedPricesMap,
  evaluateFormulasQueryAtom,
} from '../_graphql/queryEvaluateFormulas';
import { productPricingsQueryAtom } from '../_graphql/queryProductPricings';

import { atomFromQueryString } from '~/shared/state';

export const $selectedFacilityId = atomFromQueryString.string('dpFacilityId');
export const $selectedOperationType = atomFromQueryString.string<OperationType>(
  'dpOpType',
  'Purchase',
);
export const $selectedCurrency = atomFromQueryString.string<string>(
  'dpCurrency',
  'USD',
);

export const $canLoadPriceList = atom((get) => {
  const facilityId = get($selectedFacilityId);
  const operationType = get($selectedOperationType);
  const currency = get($selectedCurrency);
  return !!facilityId && !!operationType && !!currency;
});

const $defaultPriceListGetOrCreate = defaultPriceListGetOrCreateMutationAtom();

export const $remoteDefaultPriceList = atomWithQuery((get) => {
  const facilityId = get($selectedFacilityId);
  const operationType = get($selectedOperationType);
  const currency = get($selectedCurrency);
  const canLoad = get($canLoadPriceList);

  return {
    queryKey: [
      'defaultPriceListGetOrCreate',
      facilityId,
      operationType,
      currency,
    ],
    queryFn: async () => {
      if (!canLoad || !facilityId) return null;
      const store = getDefaultStore();
      return store.set($defaultPriceListGetOrCreate, {
        facilityId,
        operationType: (operationType as OperationType) ?? 'Purchase',
        currency: currency ?? 'USD',
      });
    },
    enabled: canLoad,
  };
});

export const $defaultPriceListId = atom((get) => {
  const data = get($remoteDefaultPriceList);
  return data?.data?.id ?? null;
});

export const $remoteProductPricings = productPricingsQueryAtom((get) => {
  const priceListId = get($defaultPriceListId);
  const asOfDateTime = get($asOfDateTime);

  return {
    variables: {
      priceListId: priceListId ?? '',
      priceListType: 'Default' as const,
      ...(asOfDateTime ? { asOf: asOfDateTime.toAbsoluteString() } : {}),
    },
    skip: !priceListId,
  };
});

export const $isDefaultPriceListLoading = atom((get) => {
  const data = get($remoteDefaultPriceList);
  return data?.isLoading ?? false;
});

export const $remoteEvaluatedFormulas = evaluateFormulasQueryAtom((get) => {
  const priceListId = get($defaultPriceListId);
  const asOfDateTime = get($asOfDateTime);

  return {
    variables: {
      priceListId: priceListId ?? '',
      priceListType: 'Default' as PriceListType,
      ...(asOfDateTime ? { asOf: asOfDateTime.toAbsoluteString() } : {}),
    },
    skip: !priceListId,
  };
});

export const $evaluatedPrices = atom<EvaluatedPricesMap>((get) => {
  const data = get($remoteEvaluatedFormulas);
  return data?.data ?? {};
});

export const $selectedCell = atom<SelectedCell | null>(null);

export const $asOfDateTime = atom<ZonedDateTime | null>(null);

export const $pendingTiersEffectiveAt = atom<CalendarDate | null | undefined>(
  undefined,
);
